import { useState } from "react";
import { Users, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

export const MembersDialog = ({ trip, onRefresh }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("copiloot");
  const [busy, setBusy] = useState(false);
  const members = trip.members || [];

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/trips/${trip.id}/members`, { email, role });
      toast.success(`${email} uitgenodigd als ${role}. Er is een uitnodigingsmail verstuurd — zodra die persoon inlogt met dit e-mailadres, staat de trip op het dashboard.`);
      setEmail("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    await api.delete(`/trips/${trip.id}/members/${encodeURIComponent(m.email)}`);
    toast.success("Lid verwijderd");
    onRefresh?.();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button data-testid="members-button" variant="outline" className="rounded-full active:scale-95 transition-[transform,background-color] duration-200">
          <Users className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Leden{members.length > 0 ? ` (${members.length + 1})` : ""}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Samen plannen</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Nodig je reispartner uit — die kan de trip bekijken en mee aanpassen.</p>
        <form onSubmit={invite} className="space-y-3">
          <Input data-testid="member-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="partner@voorbeeld.nl" className="rounded-xl h-11" />
          <div className="flex gap-2">
            {["copiloot", "bestuurder"].map((r) => (
              <button key={r} type="button" data-testid={`member-role-${r}`} onClick={() => setRole(r)}
                className={`px-4 py-2 rounded-full text-xs font-semibold capitalize transition-[background-color,color] duration-200 ${role === r ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/70"}`}>
                {r}
              </button>
            ))}
            <Button data-testid="member-invite-button" type="submit" disabled={busy || !email} className="ml-auto rounded-full bg-primary hover:bg-[#1B4332]">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Uitnodigen
            </Button>
          </div>
        </form>
        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {members.map((m) => (
            <div key={m.email} data-testid={`member-row-${m.email}`} className="bg-secondary/50 rounded-xl p-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{m.email}</p>
                <p className="text-xs text-muted-foreground capitalize">{m.role}</p>
              </div>
              <button data-testid={`member-remove-${m.email}`} onClick={() => remove(m)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {members.length === 0 && <p className="text-xs text-muted-foreground">Nog geen leden uitgenodigd.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
